import { forwardRef, memo, useMemo, useCallback } from "react";
import { StarIcon as Star, HeartIcon as Heart, FlameIcon as Flame, ShoppingBagIcon as ShoppingBag, ShoppingCartIcon as ShoppingCart, ZapIcon as Zap } from "./icons";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { useQuickBuy } from "@/contexts/QuickBuyContext";
import { getDefaultSizeOfferPrice, getSelectedSizeRegularPrice } from "@/lib/productPricing";

export interface Product {
  id: number | string;
  name: string;
  price: string | number;
  offer_price?: number | null;
  description?: string | null;
  rating?: number;
  image?: string | null;
  category?: string;
  category_id?: string | null;
  seo_slug?: string | null;
  stock?: number;
  sizes?: string[];
  color_images?: Record<string, string[]> | null;
  is_preorder?: boolean;
  preorder_message?: string | null;
  free_delivery?: boolean;
  size_chart?: unknown;
}

interface ProductCardProps {
  product: Product;
  isWishlisted?: boolean;
  onToggleWishlist?: (productId: string | number) => void;
}

const ProductCard = memo(forwardRef<HTMLDivElement, ProductCardProps>(({ product, isWishlisted, onToggleWishlist }, ref) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useSiteTheme();
  const { openQuickBuy } = useQuickBuy();

  const effectivePrice = getDefaultSizeOfferPrice(product);
  const regularPrice = getSelectedSizeRegularPrice(product);
  const hasDiscount = regularPrice > 0 && effectivePrice > 0 && effectivePrice < regularPrice;
  const discountPercent = hasDiscount ? Math.round(((regularPrice - effectivePrice) / regularPrice) * 100) : 0;
  const displayPrice = `৳${effectivePrice.toLocaleString()}`;
  const displayRegularPrice = `৳${regularPrice.toLocaleString()}`;
  const isOutOfStock = product.stock === 0;
  const rating = product.rating ?? 5;
  const stock = product.stock ?? -1;
  const showLowStock = !isOutOfStock && stock >= 0 && stock <= 10;

  const handleWishlist = useCallback((e: React.MouseEvent) => { e.stopPropagation(); onToggleWishlist?.(product.id); }, [onToggleWishlist, product.id]);

  const goToProduct = useCallback(() => {
    const previewDesign = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("preview_design")
      : null;
    const suffix = previewDesign ? `?preview_design=${previewDesign}` : "";
    navigate(`/product/${product.seo_slug || product.id}${suffix}`);
  }, [navigate, product.seo_slug, product.id]);

  const handleBuyNow = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    openQuickBuy(product, "buy");
  }, [openQuickBuy, product]);

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    openQuickBuy(product, "cart");
  }, [openQuickBuy, product]);

  const cardStyleMap: Record<string, string> = {
    default: "rounded-2xl md:rounded-3xl",
    rounded: "rounded-2xl md:rounded-3xl",
    flat: "rounded-2xl md:rounded-3xl",
    bordered: "rounded-2xl md:rounded-3xl",
    glass: "rounded-2xl md:rounded-3xl",
  };

  const cardClass = cardStyleMap[theme.cardStyle] || "rounded-2xl md:rounded-3xl";
  const productCardRadius = "20px";
  const isGlass = theme.cardStyle === "glass";
  const goldenColor = `hsl(${theme.cardAccent})`;
  const stars = useMemo(() => Array.from({ length: 5 }), []);

  return (
    <div
      ref={ref}
      className={`group overflow-hidden cursor-pointer relative ${cardClass} ${isOutOfStock ? "opacity-75" : ""} border-2 md:border-4`}
      style={{
        backgroundColor: isGlass ? `hsl(${theme.cardBg} / 0.7)` : `hsl(${theme.cardBg})`,
        borderStyle: "solid",
        borderColor: "hsl(var(--product-card-border))",
        borderRadius: productCardRadius,
        contain: 'layout style paint',
      }}
      onClick={goToProduct}
    >
      <div className="relative overflow-hidden aspect-[3/4]" style={{ backgroundColor: `hsl(${theme.sectionAltBg})`, borderTopLeftRadius: productCardRadius, borderTopRightRadius: productCardRadius }}>
        <img
          src={product.image || "/placeholder.svg"}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top"
        />

        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-black/80 text-white font-heading text-xs sm:text-sm tracking-widest uppercase px-4 py-2 rounded-lg">
              {t("Out of Stock")}
            </div>
          </div>
        )}

        <div className="absolute top-1.5 left-1.5 md:top-2 md:left-2 flex flex-col gap-1 z-20">
          {hasDiscount && (
            <span className="text-[9px] md:text-[11px] font-body font-bold px-1.5 md:px-2 py-0.5 rounded-full shadow-sm" style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}>
              -{discountPercent}%
            </span>
          )}
          {showLowStock && (
            <span className="text-[9px] md:text-[10px] font-body font-bold px-1.5 md:px-2 py-0.5 rounded-full flex items-center gap-0.5" style={{ backgroundColor: `hsl(${theme.badgeBg})`, color: `hsl(${theme.badgeText})` }}>
              <Flame size={9} /> Very Low Stock
            </span>
          )}
        </div>

        {product.free_delivery && (
          <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 z-20">
            <span className="text-[9px] md:text-[11px] font-body font-bold px-1.5 md:px-2 py-0.5 rounded-full shadow-sm flex items-center gap-0.5" style={{ backgroundColor: "hsl(142 70% 38%)", color: "#fff" }}>
              🚚 Free Delivery
            </span>
          </div>
        )}

        {onToggleWishlist && (
          <button onClick={handleWishlist} className={`absolute ${product.free_delivery ? "top-9 md:top-10" : "top-1.5 md:top-2"} right-1.5 md:right-2 p-1.5 rounded-full md:opacity-0 md:group-hover:opacity-100 z-20 transition-opacity`} style={{ backgroundColor: `hsl(${theme.cardBg} / 0.92)`, color: `hsl(${theme.mutedText})` }}>
            <Heart size={14} className={isWishlisted ? "fill-destructive text-destructive" : ""} />
          </button>
        )}

        {!isOutOfStock && (
          <div className="absolute bottom-2 left-2 right-2 z-30 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out hidden md:flex gap-1.5 pointer-events-none group-hover:pointer-events-auto">
            <button
              onClick={handleBuyNow}
              className="flex-[2.25] flex items-center justify-center gap-2 px-3 py-2.5 text-[12px] font-body font-extrabold tracking-[0.15em] uppercase"
              style={{
                backgroundColor: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                outline: "1px solid hsl(var(--primary))",
                outlineOffset: "3px",
                borderRadius: "2px",
                textShadow: "0 1px 1px rgba(0,0,0,0.15)",
              }}
            >
              <span>Buy Now</span>
            </button>
            <button
              onClick={handleAddToCart}
              aria-label="Add to cart"
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-[11px] font-body font-extrabold tracking-[0.1em] uppercase"
              style={{
                backgroundColor: "hsl(var(--storefront-header))",
                color: "hsl(var(--foreground))",
                outline: "1px solid hsl(var(--foreground))",
                outlineOffset: "3px",
                borderRadius: "2px",
                textShadow: "0 1px 1px rgba(255,255,255,0.4)",
              }}
            >
              <ShoppingCart size={15} strokeWidth={2} />
              <span>Cart</span>
            </button>
          </div>
        )}
      </div>

      <div className="px-3 pt-3 pb-2 sm:p-3 md:p-4">
        <h3 className="font-heading text-[11px] sm:text-sm md:text-base mb-1 line-clamp-2 leading-snug" style={{ color: `hsl(${theme.cardText})` }}>{product.name}</h3>
        <div className="flex items-center gap-0.5 mb-1.5">
          {stars.map((_, i) => (
            <Star key={i} size={10} style={{ color: i < rating ? goldenColor : `hsl(${theme.borderColor})`, fill: i < rating ? goldenColor : "none" }} />
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-price font-semibold text-xl sm:text-base md:text-lg leading-none" style={{ color: `hsl(${theme.cardText})` }}>{displayPrice}</p>
          {hasDiscount && (
            <p className="font-price text-xs sm:text-sm leading-none line-through" style={{ color: `hsl(${theme.mutedText})` }}>{displayRegularPrice}</p>
          )}
        </div>
      </div>

      {!isOutOfStock && (
        <div className="flex md:hidden gap-1.5 px-2.5 pb-2.5">
          <button
            onClick={handleBuyNow}
            className="flex-[3] flex items-center justify-center gap-2 px-3 h-9 text-[11px] font-body font-extrabold tracking-[0.15em] uppercase active:scale-[0.98] transition-transform"
            style={{
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              outline: "1px solid hsl(var(--primary))",
              outlineOffset: "2px",
              borderRadius: "2px",
              textShadow: "0 1px 1px rgba(0,0,0,0.15)",
            }}
          >
            <span>Buy Now</span>
          </button>
          <button
            onClick={handleAddToCart}
            aria-label="Add to cart"
            className="aspect-square h-9 flex items-center justify-center active:scale-[0.95] transition-transform"
            style={{
              backgroundColor: "hsl(var(--storefront-header))",
              color: "hsl(var(--foreground))",
              outline: "1px solid hsl(var(--foreground))",
              outlineOffset: "2px",
              borderRadius: "2px",
            }}
          >
            <ShoppingCart size={14} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </div>
  );
}));

ProductCard.displayName = "ProductCard";

export default ProductCard;